import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Role } from "@/types/rbac";
import { useRolePermissions } from "@/hooks/rbac/usePermissions";
import { useResources } from "@/hooks/rbac/useResources";
import { Shield, Users, Calendar, CheckCircle, XCircle, Eye, Edit } from "lucide-react";
import { format } from "date-fns";

interface RoleViewDialogProps {
  role: Role | null;
  isOpen: boolean;
  onClose: () => void;
  userCount?: number;
}

export const RoleViewDialog = ({ role, isOpen, onClose, userCount = 0 }: RoleViewDialogProps) => {
  const { data: permissions = [], isLoading: permissionsLoading } = useRolePermissions(role?.id || null);
  const { data: resources = [], isLoading: resourcesLoading } = useResources();

  if (!role) return null;

  const getPermissionIcon = (permission: string) => {
    switch (permission) {
      case 'edit':
        return <Edit className="h-4 w-4 text-primary" />;
      case 'view':
        return <Eye className="h-4 w-4 text-muted-foreground" />;
      case 'none':
      default:
        return <XCircle className="h-4 w-4 text-destructive" />;
    }
  };

  const getPermissionBadgeVariant = (permission: string) => {
    switch (permission) {
      case 'edit':
        return 'default';
      case 'view':
        return 'secondary';
      case 'none':
      default:
        return 'outline';
    }
  };

  // Create a map of resource permissions for easy lookup
  const permissionMap = permissions.reduce((acc, perm) => {
    acc[perm.resource_id] = perm.permission;
    return acc;
  }, {} as Record<string, string>);

  // Get permission stats
  const permissionStats = {
    edit: permissions.filter(p => p.permission === 'edit').length,
    view: permissions.filter(p => p.permission === 'view').length,
    none: resources.length - permissions.length
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            {role.name}
            {role.is_system && (
              <Badge variant="outline" className="text-xs">
                System Role
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Role Information */}
          <GlassCard variant="default" hover={false}>
            <GlassCardHeader>
              <GlassCardTitle className="text-lg">Role Information</GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Users:</span>
                  <span className="font-medium">{userCount}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Created:</span>
                  <span className="font-medium">
                    {format(new Date(role.created_at), 'MMM d, yyyy')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Type:</span>
                  <span className="font-medium">
                    {role.is_system ? 'System' : 'Custom'}
                  </span>
                </div>
              </div>
              
              {role.description && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Description:</p>
                  <p className="text-sm">{role.description}</p>
                </div>
              )}
            </GlassCardContent>
          </GlassCard>

          {/* Permission Summary */}
          <GlassCard variant="default" hover={false}>
            <GlassCardHeader>
              <GlassCardTitle className="text-lg">Permission Summary</GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{permissionStats.edit}</div>
                  <div className="text-sm text-muted-foreground">Full Access</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-muted-foreground">{permissionStats.view}</div>
                  <div className="text-sm text-muted-foreground">View Only</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-destructive">{permissionStats.none}</div>
                  <div className="text-sm text-muted-foreground">No Access</div>
                </div>
              </div>
            </GlassCardContent>
          </GlassCard>

          {/* Detailed Permissions */}
          <GlassCard variant="default" hover={false}>
            <GlassCardHeader>
              <GlassCardTitle className="text-lg">Resource Permissions</GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent>
              {permissionsLoading || resourcesLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-12 bg-muted animate-pulse rounded" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Resource</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Permission Level</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resources.map((resource) => {
                      const permission = permissionMap[resource.id] || 'none';
                      return (
                        <TableRow key={resource.id}>
                          <TableCell className="font-medium">{resource.name}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {resource.description || 'No description available'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getPermissionIcon(permission)}
                              <Badge variant={getPermissionBadgeVariant(permission)}>
                                {permission === 'none' ? 'No Access' : permission.charAt(0).toUpperCase() + permission.slice(1)}
                              </Badge>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </GlassCardContent>
          </GlassCard>
        </div>
      </DialogContent>
    </Dialog>
  );
};